import { useEffect, useState } from "react";
import axios from "axios";
import { RotateCcw, Search } from "lucide-react";
import { Link, Navigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import BoardWorkspace from "@/components/BoardWorkspace";
import BoardLoading from "@/components/BoardLoading";
import { useAuth } from "@/auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;

export default function DeletedProjectsPage() {
  const { canAccessBilling, loading } = useAuth();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [restoringId, setRestoringId] = useState("");
  const load = async (term = "") => {
    const response = await axios.get(`${API}/deleted-projects`, { params: { search: term } });
    setRows(response.data.rows || []);
  };
  useEffect(() => {
    if (canAccessBilling) {
      load();
    }
  }, [canAccessBilling]);
  const restore = async (project) => {
    setRestoringId(project.id);
    try {
      await axios.post(`${API}/projects/${project.id}/restore-from-deleted`);
      toast.success(`${project.project_code} restored to Not Started.`);
      await load(search);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Project could not be restored.");
    } finally {
      setRestoringId("");
    }
  };
  if (loading) {
    return <BoardLoading label="Checking archive access" />;
  }
  if (!canAccessBilling) {
    return <Navigate replace to="/master-project-board" />;
  }
  return (
    <BoardWorkspace
      eyebrow="Project archive"
      headingTestId="deleted-projects-heading"
      subtitle="Canceled projects retained with their invoices, permits, and history. Restore returns a project to Not Started."
      title="Deleted Projects"
    >
      <section className="closed-projects-toolbar" data-testid="deleted-projects-toolbar">
        <Search size={16} />
        <input
          data-testid="deleted-projects-search"
          onChange={(event) => {
            setSearch(event.target.value);
            load(event.target.value);
          }}
          placeholder="Search project number, name, customer"
          value={search}
        />
        <span data-testid="deleted-projects-count">{rows.length} projects</span>
      </section>
      <section className="board-table-panel closed-projects-table-panel" data-testid="deleted-projects-page">
        <div className="board-table-scroll">
          <table className="operations-data-table" data-testid="deleted-projects-table">
            <thead><tr><th>Project Number</th><th>Project</th><th>Customer</th><th>Cancellation reason</th><th>Restore</th></tr></thead>
            <tbody>
              {rows.map((project) => (
                <tr data-testid={`deleted-project-${project.id}`} key={project.id}>
                  <td><Link to={`/projects/${project.id}`}>{project.project_code}</Link></td>
                  <td>{project.name}</td>
                  <td>{project.client || "—"}</td>
                  <td>{project.cancellation_reason || "—"}</td>
                  <td>
                    <button data-testid={`restore-deleted-project-${project.id}`} disabled={restoringId === project.id} onClick={() => restore(project)} type="button">
                      <RotateCcw size={14} />
                      {restoringId === project.id ? "Restoring…" : "Restore"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <Toaster richColors position="bottom-right" />
    </BoardWorkspace>
  );
}